import { Component, ViewChild, ElementRef, OnInit, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FlowObjectDefinition } from '../../models/flow-object.model';
import { BaseComponent } from '../../components/base/base.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../shared/enums';
import { FlowInstanceService } from '../../services/flow-instance.service';
import { AuthService } from '../../services/auth.service';
import { Utils } from '../../shared/utils';
import { IBaseOption } from '../../models/base.model';
import { MatCheckbox } from '@angular/material/checkbox';
import { InputDataTaskForm } from '../../models/input-output-data.model';
import { IMPORTS } from './_imports';

@Component({
    selector: 'tests',
    templateUrl: './tests.component.html',
    styleUrl: './tests.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class TestsComponent extends BaseComponent implements OnInit {
    // #region [ViewChild]
    @ViewChild('configCardRef') configCardRef: ElementRef;
    @ViewChild('testContainerRef') testContainerRef: ElementRef;
    @ViewChild('testResultsContainerRef') testResultsContainerRef: ElementRef;
    @ViewChild('defaultDesRef') defaultDesRef: MatCheckbox;
    @ViewChild('defaultHomRef') defaultHomRef: MatCheckbox;
    @ViewChild('defaultTreRef') defaultTreRef: MatCheckbox;
    // #endregion

    // #region [Type props]
    Math = Math;
    // #endregion

    // #region [const]
    defaultDesFDId: string = 'f4bd1081-17a4-133f-18f4-7b74de496bf3' as const;
    defaultDesFODId: string = '860AA06C-7558-1782-F265-B7F31835FA82' as const;

    defaultHomFDId: string = 'bf240b29-5aa6-f0c1-ce9a-1c1cb1b31e6d' as const;
    defaultHomFODId: string = 'F487EBE8-E599-A559-220A-40B36950915F' as const;

    defaultTreFDId: string = 'db4896d3-ed1e-cce0-138a-0c09b5fc64aa' as const;
    defaultTreFODId: string = 'A7451EEE-7DDA-B3F0-1146-265FAD2DD218' as const;
    // #endregion

    // #region [props]
    reqs = 1;
    reqsPerSec = 1;
    secs = 60;
    flowObjectToExecute: FlowObjectDefinition;
    interval: any;
    dirSortRoundtrip = false;
    dirSortSequence = false;
    // #endregion

    // #region [signals]
    $papelId = signal(null as string);
    $flowDefinitionId = signal(null as string);
    $flowObjectDefinitionId = signal(null as string);
    $formData = signal(null as string);
    $finishedTests = signal(0);
    $elapsedTime = signal(0);
    $loadTestsResults = signal([] as any[]);
    $papelOptions = signal([] as IBaseOption[]);
    $isBootstrapFinished = signal(false);
    $areTestsRolling = signal(false);
    $isTheTestConcurrent = signal(null as boolean);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    // #endregion

    constructor(
        route: ActivatedRoute,
        router: Router,
        spinner: NgxSpinnerService,
        toastr: ToastrService,
        private flowInstanceService: FlowInstanceService,
        private authService: AuthService
    ) {
        super(route, router, spinner, toastr);
    }

    ngOnInit() {
        this.$papelOptions.set(Array.from(this.$currentUser().papeis, item => {
            return {
                value: item.guid,
                description: `${item.nome} - ${item.lotacao.nomeCurto} - ${item.lotacao.organizacao.sigla}`
            };
        }));
        this.$papelId.set(this.$currentUser().papeis[0].guid);

        this.$formData.set(JSON.stringify({
            "submit": true
        }));

        this.$flowDefinitionId.set(this.defaultDesFDId);
        this.$flowObjectDefinitionId.set(this.defaultDesFODId);

        setTimeout(() => {
            this.$isBootstrapFinished.set(true);
            setTimeout(() => {
                if (this.testContainerRef != null) {
                    (this.testContainerRef.nativeElement as HTMLElement).style.height = '100%';
                }
                this.spinner.hide();
            }, 300);
        }, 300);
    }

    async fireConcurrentReqs() {
        if (this.reqs == null || this.reqs == 0) {
            this.toastr.error('Cadê as requisições?', Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        this.$areTestsRolling.set(true);
        this.$isTheTestConcurrent.set(true);
        this.$loadTestsResults.set([]);
        this.$finishedTests.set(0);

        clearInterval(this.interval);
        this.$elapsedTime.set(0);

        let inputData = ({
            data: JSON.parse(this.$formData()),
            eDocsData: {
                signerId: this.$papelId()
            }
        }) as InputDataTaskForm;

        this.flowObjectToExecute = new FlowObjectDefinition({
            id: this.$flowObjectDefinitionId(),
            flowDefinitionId: this.$flowDefinitionId()
        });

        let elapsedStart = new Date();
        this.interval = setInterval(() => {
            this.$elapsedTime.set(new Date().getTime() - elapsedStart.getTime());
            if (this.$elapsedTime() > 0 && this.$finishedTests() == this.reqs) {
                clearInterval(this.interval);
            }
        }, 100);

        for (let i = 1; i <= this.reqs; i++) {
            let start = new Date();
            Utils.updSignal(this.$loadTestsResults, x => {
                x.push({
                    sequence: i,
                    batch: 1,
                    start: start.toISOString().split('T')[1],
                    finish: null,
                    roundtrip: null,
                    id: null,
                    message: null,
                    isSuccess: null
                });
                return x;
            });

            this.flowInstanceService.create(this.flowObjectToExecute, inputData).then(response => {
                let loadTestResult = this.$loadTestsResults().find(x => x.sequence == i);
                let finish = new Date();
                loadTestResult.finish = finish.toISOString().split('T')[1];
                loadTestResult.diff = (finish.getTime() - start.getTime()) / 1000;
                loadTestResult.roundtrip = loadTestResult.diff.toFixed(3) + 's';

                let intRes = setInterval(() => {
                    if (response != null) {
                        loadTestResult.id = response.data?.id;
                        loadTestResult.message = response.message.description;
                        loadTestResult.isSuccess = response.isSuccess;
                        clearInterval(intRes);
                    }
                }, 200);

                this.$finishedTests.set(this.$finishedTests() + 1);

                if (this.$finishedTests() == this.reqs) {
                    this.$areTestsRolling.set(false);
                    this.$isTheTestConcurrent.set(null);
                }
            });
        }
    }

    async fireScaledReqs() {
        if (this.reqsPerSec == null || this.reqsPerSec == 0) {
            this.toastr.error('Cadê as requisições?', Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        } else if (this.secs == null || this.secs == 0) {
            this.toastr.error('Cadê os segundos?', Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        this.$areTestsRolling.set(true);
        this.$isTheTestConcurrent.set(false);
        this.$loadTestsResults.set([]);
        this.$finishedTests.set(0);

        clearInterval(this.interval);
        this.$elapsedTime.set(0);

        let inputData = ({
            data: JSON.parse(this.$formData()),
            eDocsData: {
                signerId: this.$papelId()
            }
        }) as InputDataTaskForm;

        this.flowObjectToExecute = new FlowObjectDefinition({
            id: this.$flowObjectDefinitionId(),
            flowDefinitionId: this.$flowDefinitionId()
        });

        let elapsedStart = new Date();
        this.interval = setInterval(() => {
            this.$elapsedTime.set(new Date().getTime() - elapsedStart.getTime());
            if (this.$elapsedTime() > 0 && this.$finishedTests() == this.reqsPerSec * this.secs) {
                clearInterval(this.interval);
            }
        }, 100);

        for (let j = 0; j < this.secs; j++) {
            setTimeout(() => {
                for (let i = 1; i <= this.reqsPerSec; i++) {
                    let start = new Date();
                    Utils.updSignal(this.$loadTestsResults, x => {
                        x.push({
                            sequence: (j * this.reqsPerSec) + i,
                            batch: j + 1,
                            start: start.toISOString().split('T')[1],
                            finish: null,
                            roundtrip: null,
                            id: null,
                            message: null,
                            isSuccess: null
                        });
                        return x;
                    });

                    this.flowInstanceService.create(this.flowObjectToExecute, inputData).then(response => {
                        let loadTestResult = this.$loadTestsResults().find(x => x.sequence == (j * this.reqsPerSec) + i);
                        let finish = new Date();
                        loadTestResult.finish = finish.toISOString().split('T')[1];
                        loadTestResult.diff = (finish.getTime() - start.getTime()) / 1000;
                        loadTestResult.roundtrip = loadTestResult.diff.toFixed(3) + 's';

                        let intRes = setInterval(() => {
                            if (response != null) {
                                loadTestResult.id = response.data?.id;
                                loadTestResult.message = response.message.description;
                                loadTestResult.isSuccess = response.isSuccess;
                                clearInterval(intRes);
                            }
                        }, 200);

                        this.$finishedTests.set(this.$finishedTests() + 1);

                        if (this.$finishedTests() == this.reqsPerSec * this.secs) {
                            this.$areTestsRolling.set(false);
                            this.$isTheTestConcurrent.set(null);
                        }
                    });
                }
            }, 1000 * j);
        }
    }

    toggleConfig() {
        this.testContainerRef.nativeElement.querySelectorAll('.toggle').forEach(x => x.classList.toggle('off'));
    }

    toggleAll() {
        this.configCardRef.nativeElement.classList.toggle('min');
        this.configCardRef.nativeElement.querySelectorAll('h6 ~ *').forEach(x => x.classList.toggle('off'));
    }

    sortRoundtrip() {
        if (this.$loadTestsResults().length == 0) return;

        if (this.dirSortRoundtrip) {
            this.$loadTestsResults.set(this.$loadTestsResults().toSorted((a, b) => b.diff - a.diff));
        } else {
            this.$loadTestsResults.set(this.$loadTestsResults().toSorted((a, b) => a.diff - b.diff));
        }

        this.testResultsContainerRef.nativeElement.querySelectorAll('.sortable.roundtrip').forEach(x => x.classList.add('on'));
        this.testResultsContainerRef.nativeElement.querySelectorAll('.sortable.sequence').forEach(x => x.classList.remove('on'));

        this.dirSortRoundtrip = !this.dirSortRoundtrip;
    }

    sortSequence() {
        if (this.$loadTestsResults().length == 0) return;

        if (this.dirSortSequence) {
            this.$loadTestsResults.set(this.$loadTestsResults().toSorted((a, b) => b.sequence - a.sequence));
        } else {
            this.$loadTestsResults.set(this.$loadTestsResults().toSorted((a, b) => a.sequence - b.sequence));
        }

        this.testResultsContainerRef.nativeElement.querySelectorAll('.sortable.sequence').forEach(x => x.classList.add('on'));
        this.testResultsContainerRef.nativeElement.querySelectorAll('.sortable.roundtrip').forEach(x => x.classList.remove('on'));

        this.dirSortSequence = !this.dirSortSequence;
    }

    setDefaultDes(event) {
        if (event) {
            this.defaultHomRef.checked = false;
            this.defaultTreRef.checked = false;
            this.$flowDefinitionId.set(this.defaultDesFDId);
            this.$flowObjectDefinitionId.set(this.defaultDesFODId);
        } else {
            this.$flowDefinitionId.set(null);
            this.$flowObjectDefinitionId.set(null);
        }
    }

    setDefaultHom(event) {
        if (event) {
            this.defaultDesRef.checked = false;
            this.defaultTreRef.checked = false;
            this.$flowDefinitionId.set(this.defaultHomFDId);
            this.$flowObjectDefinitionId.set(this.defaultHomFODId);
        } else {
            this.$flowDefinitionId.set(null);
            this.$flowObjectDefinitionId.set(null);
        }
    }

    setDefaultTre(event) {
        if (event) {
            this.defaultDesRef.checked = false;
            this.defaultHomRef.checked = false;
            this.$flowDefinitionId.set(this.defaultTreFDId);
            this.$flowObjectDefinitionId.set(this.defaultTreFODId);
        } else {
            this.$flowDefinitionId.set(null);
            this.$flowObjectDefinitionId.set(null);
        }
    }

    // #region [===== private methods =====]
    // #endregion
}
